import * as React from "react";
// import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import lodashGet from "lodash/get";
import { useDebounce } from "use-debounce";

import { showNotification } from "jazasoft";
import { minValue, required } from "@jazasoft/mui-table";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSafeState, useSetting, useTime, useUnit } from "../../hooks";
// import { format } from "../../utils/dateUtils";
import { getDistinctValues, getStyleColor, isEmpty, isFalse, isTrue, listFromCsv, toFixed, toFixedValue } from "../../utils/helpers";

import { Entity, OState, QcStatus } from "../../utils/types";
import { colorLightGreen, colorOrange, colorTomatoRed } from "../../utils/colors";

import { LabeledCheckBox } from "./ShadeRowRenderer";
import { generateGroupingReportData, getDeviation, getFinalOption, shrinkageRange, getReadyToGroupRows, getGroupOrderRows } from "./groupingHelper";
import SettingKeys from "../../utils/SettingKeys";
import useWarehouse from "../../hooks/useWarehouse";
import { cbSortFlow } from "../../helpers/orderHelper";
import { DEFAULT_SEPARATOR, LotType, OrderFlowType, UOM } from "../../constants";
import { convert } from "../../helpers/unitConversionHelper";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const getGroupFabricQty = (group, uom, conversionFactorList) => {
  if (isEmpty(group) || isEmpty(uom)) return "";
  return (
    `${toFixed(group.totalLength)} m` +
    (uom === UOM.METER.value
      ? ""
      : uom === UOM.KILOGRAM.value
      ? `\n(${toFixed(group.totalWeight)} kg)`
      : `\n(${toFixed(convert({ input: group.totalLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList }))} ${UomCodeFromValue[uom]})`)
  );
};

const renderState = (value, _, row, onClick) =>
  isEmpty(value) ? (
    value
  ) : (
    <div
      style={{
        padding: "3px 6px",
        borderRadius: 4,
        maxWidth: 100,
        margin: "auto",
        backgroundColor: row.lotQty > 0 && value >= row.lotQty ? colorLightGreen.dark : value > 0 ? colorOrange.dark : colorTomatoRed.dark,
        color: "white"
      }}
      onClick={() => onClick && onClick(row)}>
      {value} {UomCodeFromValue[row.uom]}
    </div>
  );

const useGroupingView = props => {
  const { id } = props;

  const [version, setVersion] = useSafeState(1);
  const [tabIdx, setTabIdx] = useSafeState(0);
  const [selectedKeys, setSelectedKeys] = useSafeState([]); // For Tab: Ready To Plan
  const [selectedKey, setSelectedKey] = useSafeState(null); // For Tab: Planned Orders
  const [shadeState, setShadeState] = useSafeState({ columns: [], rows: [] });
  const [errorMessage, setErrorMessage] = useSafeState(null);
  const [setting, setSetting] = useSafeState({});
  const [fabRows, setFabRows] = useSafeState();
  const [shrinkageOptions, setShrinkageOptions] = useSafeState([]);
  const [widthOptionState, setWidthOptionState] = useSafeState({});
  const [extRollList, setExtRollList] = useSafeState();
  const [extDrawerState, setExtDrawerState] = useSafeState({ open: false, row: null });

  const { columns: shadeColumns, rows: shadeRows } = shadeState;

  const { allUnitList } = useUnit();
  const { utc_offset } = useTime();
  const history = useHistory();
  const dispatch = useDispatch();

  const [mutate, { loading: busy }] = useMutation();
  const [download] = useMutation({ rawResponse: true });

  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ROLLS);
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const minRollSplitLength = useSetting(SettingKeys.MIN_ROLL_SPLIT_LENGTH);
  const prefillAssociationQty = useSetting(SettingKeys.PREFILL_ASSOCIATION_QTY);
  const fpoWarehouseVal = useSetting(SettingKeys.FABRIC_GROUPING_FROM_FPO_WAREHOUSE);
  const conversionFactor = useSetting(SettingKeys.UOM_CONVERSION_FACTOR);
  const allowMultiInvoiceAutoAllocation = useSetting(SettingKeys.ALLOW_MULTI_INVOICE_AUTO_ALLOCATION);
  const conversionFactorList = JSON.parse(conversionFactor || "[]");
  const prefillAllocationQty = isTrue(prefillAssociationQty);
  const fpoWarehouse = isTrue(fpoWarehouseVal);

  const messages = useSelector(state => state?.i18n?.messages || {});
  const tenantName = useSelector(state => state?.jazasoft?.auth?.tenant?.name);
  const { allWhList, whList, whLoading } = useWarehouse();
  const whCodes = whList.map(e => e.code);

  const { data: order = {}, loading } = useQuery({ url: `orders/${id}`, params: { page: "grouping" }, version });
  const { data: ledgerData = [] } = useQuery(id && { url: `ledgers`, params: { search: `order.id==${id}`, size: 10000 }, version });

  const groupOrder = useDeepMemo(() => {
    if (isEmpty(selectedKey) || tabIdx !== 0 || isEmpty(order)) return null;
    const [flowId] = `${selectedKey}`.split("-");
    return order.orderFlowList.find(flow => `${flow.id}` === flowId);
  }, [order, selectedKey, tabIdx]);

  const [groupOrderId] = useDebounce(groupOrder?.id, 1000);

  const { data: groupList = [] } = useQuery(
    !isEmpty(groupOrderId) && { url: `groups`, params: { search: `orderFlowList.id==${groupOrderId}` }, version }
  );

  const { data: allocRollInfoList = [] } = useQuery(
    !isEmpty(order) && {
      url: `rolls`,
      params: {
        search: `invStatus=in=('Unallocated', 'Allocated', 'Planned');orderId==${id};parentId=='NULL'`,
        size: 100000
      },
      version
    }
  );

  //* FlowInfo Table Rows and Columns *//
  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    let rowList = [];
    if (tabIdx === 0) {
      const partSerial = !isEmpty(productParts) ? listFromCsv(productParts).reduce((acc, part, idx) => ({ ...acc, [part]: idx }), {}) : {};

      rowList = getGroupOrderRows(order);

      const hasParent = rowList.some(row => row.isParent);
      rowList = hasParent
        ? rowList
            .filter(row => row.isParent)
            .flatMap(parent => {
              const children = rowList.filter(row => row.parentKey === parent.key);
              return [parent, ...children.sort((a, b) => cbSortFlow(a.groupOrder, b.groupOrder, partSerial))];
            })
        : rowList.sort((a, b) => cbSortFlow(a.groupOrder, b.groupOrder, partSerial));

      rowList = rowList.map(row => {
        const uomList = getDistinctValues(
          row.flowInfoList?.flatMap(fi => fi.partList || []).map(p => (p.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : p.uom)) || []
        );
        const uom = uomList?.length === 1 ? uomList[0] : UOM.METER.value;
        const finalUom = uom === UOM.GRAM.value ? UOM.KILOGRAM.value : uom;
        return {
          ...row,
          uom: finalUom,
          allocated:
            uom === UOM.KILOGRAM.value
              ? toFixed(row.groupOrder?.effAllocatedWeight || 0)
              : convert({ input: toFixed(row.groupOrder?.effAllocatedLength || 0), uomIn: UOM.METER.value, uomOut: uom, conversionFactorList })
        };
      });
    } else {
      rowList = getReadyToGroupRows(order);
      rowList = rowList
        .map(flowInfo => {
          const ledgerList = ledgerData
            // .filter(e => isEmpty(whCodes) || isEmpty(e.fabricItem?.warehouse) || whCodes.includes(e.fabricItem.warehouse))
            .filter(
              l =>
                l.productId === flowInfo.product?.id &&
                l.style === flowInfo.style &&
                l.styleColor === flowInfo.color &&
                l.part === flowInfo.part?.effPartName
            )
            .filter(l => isEmpty(flowInfo.parentColor) || flowInfo.parentColor === l.parentStyleColor);

          const groupOrderList = order?.orderFlowList
            ?.filter(of => of.type === OrderFlowType.GROUPING)
            ?.filter(
              of =>
                of.productIdList?.includes(flowInfo.product?.id) &&
                of.styleColorList?.includes(flowInfo.styleColor) &&
                isEmpty(of.parentColor) &&
                of.part === flowInfo.part?.effPartName
            );
          const preRunCutOrderList = order?.orderFlowList
            ?.filter(of => of.type === OrderFlowType.CUTPLAN)
            ?.filter(
              of =>
                of.productIdList?.includes(flowInfo.product?.id) &&
                of.styleColorList?.includes(flowInfo.styleColor) &&
                isEmpty(of.parentColor) &&
                of.part === flowInfo.part?.effPartName &&
                of.lotType === LotType.PRE_RUN &&
                isEmpty(of.parentIds)
            );
          const consUom = flowInfo?.uom;

          const maxLot = flowInfo?.groupOrderList?.map(e => e.lot).sort((a, b) => b - a)[0] || 0;
          const totalAssociation = ledgerList
            .map(l =>
              convert({
                input: l.ledgerQty,
                uomIn: l.fabricItem?.uomLength,
                uomOut: consUom,
                gsm: l.fabricItem?.orderingGsm,
                widthInCm: l.fabricItem?.width,
                conversionFactorList
              })
            )
            .reduce((acc, qty) => acc + (qty || 0), 0);

          const groupOrderIds = groupOrderList?.map(e => e.id) || [];
          const fpoGroupOrderIds = flowInfo?.groupOrderList?.map(e => e.id) || [];
          const groupedLength = allocRollInfoList
            .filter(ri => groupOrderIds.includes(ri.groupOrderId) && ri.styleColor === flowInfo.styleColor)
            .reduce((acc, ri) => acc + ri.length, 0);
          const groupedWeight = allocRollInfoList
            .filter(ri => groupOrderIds.includes(ri.groupOrderId) && ri.styleColor === flowInfo.styleColor)
            .reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const fpoGroupedLength = allocRollInfoList
            .filter(ri => fpoGroupOrderIds.includes(ri.groupOrderId) && ri.styleColor === flowInfo.styleColor)
            .reduce((acc, ri) => acc + ri.length, 0);
          const fpoGroupedWeight = allocRollInfoList
            .filter(ri => fpoGroupOrderIds.includes(ri.groupOrderId) && ri.styleColor === flowInfo.styleColor)
            .reduce((acc, ri) => acc + (ri.weight || 0), 0);

          const allocatedLength = isEmpty(flowInfo?.parentKey)
            ? ledgerList.reduce((acc, e) => acc + e.allocatedLength || 0, 0)
            : flowInfo?.groupOrderList?.reduce((acc, e) => acc + (e.effAllocatedLength || 0), 0);
          const allocatedWeight = isEmpty(flowInfo?.parentKey)
            ? ledgerList.reduce((acc, e) => acc + e.allocatedWeight || 0, 0)
            : flowInfo?.groupOrderList?.reduce((acc, e) => acc + (e.effAllocatedWeight || 0), 0);
          const allocated =
            consUom === UOM.KILOGRAM.value
              ? allocatedWeight
              : convert({ input: allocatedLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

          const finalGroupedLength = isEmpty(flowInfo?.parentKey) ? groupedLength : fpoGroupedLength;
          const finalGroupedWeight = isEmpty(flowInfo?.parentKey) ? groupedWeight : fpoGroupedWeight;
          const finalGroupedQty =
            consUom === UOM.KILOGRAM.value
              ? finalGroupedWeight
              : convert({ input: finalGroupedLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

          const preRunLength = preRunCutOrderList?.reduce((acc, e) => acc + (e.effAllocatedLength || 0), 0) || 0;
          const preRunWeight = preRunCutOrderList?.reduce((acc, e) => acc + (e.effAllocatedWeight || 0), 0) || 0;
          const preRunQty =
            consUom === UOM.KILOGRAM.value
              ? preRunWeight
              : convert({ input: preRunLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

          const itemCodeList = getDistinctValues(ledgerList.map(e => e.fabric?.itemCode).filter(e => !isEmpty(e)));
          const itemColorList = getDistinctValues(ledgerList.map(e => e.fabricItem?.effFabColor).filter(e => !isEmpty(e)));

          console.log(
            `Order Details Row: FPO= ${flowInfo.fpo}, StyleColor= ${flowInfo.styleColor}, Association Qty = ${totalAssociation}, Allocated Qty = ${allocated}, Grouped Qty = ${finalGroupedQty}, Pre-Run Qty = ${preRunQty}`
          );
          return {
            ...flowInfo,
            lot: maxLot + 1,
            association: toFixed(totalAssociation),
            allocated: toFixed(allocated),
            groupedQty: toFixed(finalGroupedQty),
            preRunQty: toFixed(preRunQty),
            itemCodeList,
            itemColorList
          };
        })
        .filter(e => e.isParent || (e.association - e.groupedQty - e.preRunQty > 1 && !isEmpty(e.fpo)));
    }
    return rowList.map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
  }, [order, ledgerData, utc_offset, tabIdx, allocRollInfoList, conversionFactorList]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      {
        dataKey: "unitId",
        title: "Unit",
        render: value => allUnitList?.find(u => u.unitId === value)?.name || value || "-",
        filterOptions: { filter: true },
        highlight: true
      },
      { dataKey: "fpo", title: "FPO", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "effPartName", title: "Part", filterOptions: { filter: true }, highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "FG Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "lot", title: "Lot", render: value => (isEmpty(value) ? "" : `Lot${value}`), highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "inseam", title: "Inseam", render: (value, shortValue) => shortValue || "-", highlight: true },
      {
        dataKey: "destination",
        title: "Destination",
        render: (value, shortValue) => shortValue || "-",
        filterOptions: { filter: true },
        highlight: true
      },
      {
        dataKey: "deliveryDate",
        title: "Delivery Date",
        render: (value, shortValue) => shortValue || "-",
        filterOptions: { filter: true },
        highlight: true
      },
      {
        dataKey: "orderQty",
        title: "Order Qty",
        align: "right",
        render: (value, _, row) => value || "-"
      },
      { dataKey: "fpoQty", title: "FPO Qty", align: "right" },
      { dataKey: "extra", title: "Extra (%)", align: "right", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      //       { dataKey: "fpoExtra", title: "Extra Cut (%)", align: "right", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      {
        dataKey: "ocu",
        title: "BOM CU",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${toFixedValue(value, 3)} ${UomCodeFromValue[row.uom]}`)
      },
      {
        dataKey: "association",
        title: "Total Association",
        align: "right",
        render: (value, _, row) => (row.isFpo || !value ? "" : `${toFixed(value, 3)} ${UomCodeFromValue[row.uom]}`)
      },
      // { dataKey: "allocated", title: "Allocated Qty", align: "right", render: (value, _, row) => (row.isFpo ? "" : value) },
      {
        dataKey: "allocated",
        title: "Allocated Qty",
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "" : `${toFixed(value || 0, 3)} ${UomCodeFromValue[row.uom]}`)
      },
      {
        dataKey: "manuallyAllocated",
        title: "Manually \nAllocated Qty",
        align: "right",
        render: (value, _, row) => `${toFixed(value || 0, 3)} ${UomCodeFromValue[row.uom]}`
      }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.manuallyAllocated) || e.manuallyAllocated === 0)) {
      cols = cols.filter(c => c.dataKey !== "manuallyAllocated");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => !["unitId", "fpo", "fpoQty"].includes(c.dataKey));
    }

    if (tabIdx === 1) {
      cols = cols.filter(c => !["lotValue"].includes(c.dataKey));
    } else {
      cols = cols.filter(c => !["lot", "association", "manuallyAllocated", "ocu"].includes(c.dataKey));
    }

    return cols;
  }, [tabIdx, flowInfoRows, allUnitList]);
  //* FlowInfo Table Rows and Columns *//

  //** Redirect to Grouping Tab if Group Order not created yet **/
  useDeepEffect(() => {
    if (isEmpty(order) || loading) return;
    if (tabIdx === 0 && isEmpty(flowInfoRows)) {
      setTabIdx(1);
    }
  }, [order, loading]);

  const selectedRows = flowInfoRows.filter(row => selectedKeys.includes(row.key) && !row.isParent);

  const ledgerList = useDeepMemo(() => {
    if (whLoading) return [];
    if (tabIdx === 1) {
      return selectedRows.flatMap(row => {
        return ledgerData
          .filter(e => isEmpty(whCodes) || isEmpty(e.fabricItem?.warehouse) || whCodes.includes(e.fabricItem.warehouse))
          .filter(l => l.productId === row.product?.id && l.style === row.style && l.styleColor === row.color && l.part === row.part?.effPartName)
          .filter(l => isEmpty(row.parentColor) || row.parentColor === l.parentStyleColor);
      });
    } else {
      const groupOrder = flowInfoRows.find(e => e.key === selectedKey)?.groupOrder;
      if (!isEmpty(groupOrder)) {
        const styleColorList = groupOrder.styleColorList || [];
        return ledgerData
          .filter(
            l =>
              groupOrder.productIdList?.includes(l.productId) &&
              styleColorList.includes(getStyleColor(l.style, l.styleColor)) &&
              l.part === groupOrder.part
          )
          .filter(l => isEmpty(groupOrder.parentColor) || groupOrder.parentColor === l.parentStyleColor);
      }
    }
  }, [ledgerData, flowInfoRows, selectedKeys, selectedKey, tabIdx, whCodes, whLoading]);

  const fabricItemIds = getDistinctValues(ledgerList?.map(e => e.fabricItem?.id).filter(id => !isEmpty(id)));

  const { data: rollInfoData = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: `rolls`,
      params: {
        search: `invStatus=in=('Unallocated', 'Allocated', 'Planned', 'Issued');fabricItem.id=in=(${fabricItemIds.join(
          ","
        )});parentId=='NULL';active=in=('TRUE', 'FALSE')`,
        size: 100000
      },
      version
    }
  );
  const rollInfoList = rollInfoData.filter(ri => isTrue(ri.active));

  const { data: externalAppList = [] } = useQuery({ url: `externalApps`, params: { size: 1000 } });

  const extRollOpList = externalAppList
    ?.flatMap(externalApp => externalApp?.integrationOperationList?.map(op => ({ ...op, externalApp })) || [])
    .filter(op => isTrue(op.enabled) && op.entityId === "ExternalRoll");
  const extRollOp = isEmpty(order?.externalIds) ? {} : extRollOpList.find(op => op.externalApp?.appId === "WFX");

  useDeepEffect(() => {
    if (isEmpty(extRollOp) || isEmpty(rollInfoList)) return;

    const headers = JSON.parse(extRollOp?.headers || "{}");
    const authConfig = !isEmpty(extRollOp?.externalApp?.authConfig) ? JSON.parse(extRollOp?.externalApp?.authConfig) : {};
    const options = {
      url: extRollOp.url,
      method: extRollOp.method,
      headers: headers,
      data: {
        AuthKey: authConfig?.apiKey,
        rollInfoList: rollInfoList.map(ri => ({ rollNo: ri.rollNo }))
      }
    };
    // axios
    //   .request(options)
    //   .then(response => {
    //     if (response?.data?.Status === "Success" && !isEmpty(response.data?.ResponseData)) {
    //       setExtRollList(response.data?.ResponseData || []);
    //     } else {
    //       console.log({ options, errorMessage: response.data?.ErrorMsg });
    //     }
    //   })
    //   .catch(error => {
    //     console.log({ error });
    //   });
    const backendOptions = {
      url: "wfx/roll-status",
      method: "post",
      data: options
    };

    mutate(backendOptions, {
      onSuccess: response => {
        if (response?.Status === "Success" && !isEmpty(response?.ResponseData)) {
          setExtRollList(response?.ResponseData || []);
        } else {
          console.log({ options, errorMessage: response?.ErrorMsg });
        }
      },
      onError: error => {
        console.log({ error });
      }
    });
  }, [rollInfoList, extRollOp]);

  const onExtClick = row => {
    setExtDrawerState({ open: true, availableRollList: row?.availableRollList || [] });
  };

  //* FabricInfo Table Rows and Columns *//
  const fabricInfoRows = useDeepMemo(() => {
    if (tabIdx === 1) {
      const selectedRows = flowInfoRows.filter(row => selectedKeys.includes(row.key) && !row.isParent);
      if (isEmpty(selectedRows)) return [];

      const styleIds = getDistinctValues(selectedRows.map(e => `${e.style}-${e.color}`));
      return styleIds.map(styleId => {
        const fiRowList = selectedRows.filter(e => `${e.style}-${e.color}` === styleId);
        const flowInfo = fiRowList[0];

        const ledgers = ledgerList.filter(l => l.style === flowInfo.style && l.styleColor === flowInfo.color) || [];
        const allocOrder = flowInfo.allocOrder || {};
        const fpoGroupOrderList = flowInfo.groupOrderList || [];
        const fpoGroupOrderIds = fpoGroupOrderList.map(e => e.id);
        const hasManualAllocation = !isEmpty(allocOrder) && allocOrder?.fabricAllocation === "Manual";

        const groupOrderList = order?.orderFlowList
          ?.filter(of => of.type === OrderFlowType.GROUPING)
          ?.filter(
            of =>
              of.productIdList?.includes(flowInfo.product?.id) &&
              of.styleColorList?.includes(flowInfo.styleColor) &&
              isEmpty(of.parentColor) &&
              of.part === flowInfo.part?.effPartName
          );
        const groupOrderIds = groupOrderList?.map(e => e.id) || [];
        const preRunCutOrderList = order?.orderFlowList
          ?.filter(of => of.type === OrderFlowType.CUTPLAN)
          ?.filter(
            of =>
              of.productIdList?.includes(flowInfo.product?.id) &&
              of.styleColorList?.includes(flowInfo.styleColor) &&
              isEmpty(of.parentColor) &&
              of.part === flowInfo.part?.effPartName &&
              of.lotType === LotType.PRE_RUN
          );
        const preRunCutOrderIds = preRunCutOrderList?.map(e => e.id) || [];

        const fabricItemMap = ledgers.filter(l => !isEmpty(l.fabricItem)).reduce((acc, e) => ({ ...acc, [e.fabricItem.id]: e.fabricItem }), {});
        const fabricItemIds = Object.keys(fabricItemMap).map(id => Number(id));
        const ledger = ledgers[0];

        const itemRollList = rollInfoList.filter(ri => fabricItemIds.includes(ri.fabricItemId));

        const fpoQty = flowInfo.fpoQty || flowInfo.orderQty || 0;
        const fpoExtra = flowInfo.fpoExtra || 0;
        const extraFpoQty = toFixed((1 + 0.01 * fpoExtra) * fpoQty, 0);
        const bomCu = flowInfo.ocu || 0;
        const fabricRequired = fpoQty * bomCu;
        const extraFabricRequired = extraFpoQty * bomCu;

        const consUom = flowInfo.uom;
        const totalAssociation = ledgers
          .map(l =>
            convert({
              input: l.ledgerQty,
              uomIn: l.fabricItem?.uomLength,
              uomOut: consUom,
              gsm: l.fabricItem?.orderingGsm,
              widthInCm: l.fabricItem?.width,
              conversionFactorList
            })
          )
          .reduce((acc, qty) => acc + (qty || 0), 0);

        const allocatedRolls = itemRollList.filter(
          ri => !isEmpty(ri.allocOrderId) && ri.allocOrderId === allocOrder?.id && isEmpty(ri.groupOrderId) && isEmpty(ri.cutOrderId)
        );
        const allocatedLength = itemRollList
          .filter(ri => !isEmpty(ri.allocOrderId) && ri.allocOrderId === allocOrder?.id && isEmpty(ri.groupOrderId) && isEmpty(ri.cutOrderId))
          .reduce((acc, ri) => acc + ri.length, 0);
        const allocatedWeight = itemRollList
          .filter(ri => !isEmpty(ri.allocOrderId) && ri.allocOrderId === allocOrder?.id && isEmpty(ri.groupOrderId) && isEmpty(ri.cutOrderId))
          .reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const allocated =
          consUom === UOM.KILOGRAM.value
            ? allocatedWeight
            : convert({ input: allocatedLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });
        const manuallyAllocatedLength = hasManualAllocation ? allocatedLength : 0;
        const manuallyAllocatedWeight = hasManualAllocation ? allocatedWeight : 0;
        const manuallyAllocated =
          consUom === UOM.KILOGRAM.value
            ? manuallyAllocatedWeight
            : convert({ input: manuallyAllocatedLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

        const fpoGroupedLength = itemRollList
          .filter(
            ri =>
              !isEmpty(ri.groupOrderId) && fpoGroupOrderIds.includes(ri.groupOrderId) && ri.style === flowInfo.style && ri.color === flowInfo.color
          )
          .reduce((acc, ri) => acc + ri.length, 0);
        const fpoGroupedWeight = itemRollList
          .filter(
            ri =>
              !isEmpty(ri.groupOrderId) && fpoGroupOrderIds.includes(ri.groupOrderId) && ri.style === flowInfo.style && ri.color === flowInfo.color
          )
          .reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const fpoGroupedQty =
          consUom === UOM.KILOGRAM.value
            ? fpoGroupedWeight
            : convert({ input: fpoGroupedLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

        const groupedLength = itemRollList
          .filter(
            ri => !isEmpty(ri.groupOrderId) && groupOrderIds.includes(ri.groupOrderId) && ri.style === flowInfo.style && ri.color === flowInfo.color
          )
          .reduce((acc, ri) => acc + ri.length, 0);
        const groupedWeight = itemRollList
          .filter(
            ri => !isEmpty(ri.groupOrderId) && groupOrderIds.includes(ri.groupOrderId) && ri.style === flowInfo.style && ri.color === flowInfo.color
          )
          .reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const groupedQty =
          consUom === UOM.KILOGRAM.value
            ? groupedWeight
            : convert({ input: groupedLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

        const preRunLength = itemRollList
          .filter(ri => ri.style === flowInfo.style && ri.color === flowInfo.color)
          .filter(ri => isEmpty(ri.groupOrderId) && !isEmpty(ri.cutOrderId) && preRunCutOrderIds.includes(ri.cutOrderId))
          .reduce((acc, ri) => acc + ri.length, 0);
        const preRunWeight = itemRollList
          .filter(ri => ri.style === flowInfo.style && ri.color === flowInfo.color)
          .filter(ri => isEmpty(ri.groupOrderId) && !isEmpty(ri.cutOrderId) && preRunCutOrderIds.includes(ri.cutOrderId))
          .reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const preRunQty =
          consUom === UOM.KILOGRAM.value
            ? preRunWeight
            : convert({ input: preRunLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

        const balanceAssociation = totalAssociation - groupedQty - preRunQty;

        let availableRollList = hasManualAllocation ? allocatedRolls : itemRollList.filter(ri => isEmpty(ri.groupOrderId) && isEmpty(ri.cutOrderId));
        const hasManualPattern = availableRollList.some(e => !isEmpty(e.warpPattern) && !isEmpty(e.weftPattern));
        availableRollList = hasManualPattern ? availableRollList.filter(e => !isEmpty(e.warpPattern) && !isEmpty(e.weftPattern)) : availableRollList;

        const availableLength = availableRollList.reduce((acc, e) => acc + e.length, 0);
        const availableWeight = availableRollList.reduce((acc, e) => acc + (e.weight || 0), 0);
        const available =
          consUom === UOM.KILOGRAM.value
            ? availableWeight
            : convert({ input: availableLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

        const totalAvailableLength = itemRollList
          .filter(ri => isEmpty(ri.groupOrderId) && isEmpty(ri.cutOrderId))
          .reduce((acc, e) => acc + e.length, 0);
        const totalAvailableWeight = itemRollList
          .filter(ri => isEmpty(ri.groupOrderId) && isEmpty(ri.cutOrderId))
          .reduce((acc, e) => acc + (e.weight || 0), 0);
        const totalAvailable =
          consUom === UOM.KILOGRAM.value
            ? totalAvailableWeight
            : convert({ input: totalAvailableLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

        const shadeReadyLength = availableRollList.filter(ri => !isEmpty(ri.shade)).reduce((acc, ri) => acc + ri.length, 0);
        const shadeReadyWeight = availableRollList.filter(ri => !isEmpty(ri.shade)).reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const shadeReady =
          consUom === UOM.KILOGRAM.value
            ? shadeReadyWeight
            : convert({ input: shadeReadyLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });
        const shrinkageReadyLength = availableRollList
          .filter(ri => !isEmpty(ri.warpShrPercent) && !isEmpty(ri.weftShrPercent))
          .reduce((acc, ri) => acc + ri.length, 0);
        const shrinkageReadyWeight = availableRollList
          .filter(ri => !isEmpty(ri.warpShrPercent) && !isEmpty(ri.weftShrPercent))
          .reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const shrinkageReady =
          consUom === UOM.KILOGRAM.value
            ? shrinkageReadyWeight
            : convert({ input: shrinkageReadyLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });
        const qcReadyLength = availableRollList.filter(ri => ri.qcStatus === QcStatus.PASS).reduce((acc, ri) => acc + ri.length, 0);
        const qcReadyWeight = availableRollList.filter(ri => ri.qcStatus === QcStatus.PASS).reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const qcReady =
          consUom === UOM.KILOGRAM.value
            ? qcReadyWeight
            : convert({ input: qcReadyLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

        const extAvailableRollList = availableRollList.filter(ri => {
          const extRoll = extRollList?.find(e => e.WFXStockMovementNo === ri.rollNo);
          const uom = fabricItemMap[ri.fabricItemId]?.uomLength;
          const length =
            uom === UOM.KILOGRAM.value ? ri.weight : convert({ input: ri.length, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });
          const extLength = extRoll?.StockUnits || 0;
          return extRoll?.Status === "Available" && extRoll?.SiteCode === ri.currWarehouse && Math.abs(length - extLength) <= 0.1;
        });
        const extAvailableLength = extAvailableRollList.reduce((acc, ri) => acc + ri.length, 0);
        const extAvailableWeight = extAvailableRollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const extAvailable =
          consUom === UOM.KILOGRAM.value
            ? extAvailableWeight
            : convert({ input: extAvailableLength, uomIn: UOM.METER.value, uomOut: consUom, conversionFactorList });

        const lotQty = hasManualAllocation ? manuallyAllocated : available; // Fabric Available for current Lot
        let allocationQty = manuallyAllocated > 0 ? Math.min(lotQty, balanceAssociation) : Math.min(lotQty, balanceAssociation);
        if (!isEmpty(extRollOp)) {
          allocationQty = Math.min(allocationQty, extAvailable);
        }

        console.log(
          `Fabric Info Row: Total Association = ${toFixed(totalAssociation, 1)}, Balance Association = ${toFixed(
            balanceAssociation,
            1
          )},Total Available = ${toFixed(totalAvailable)},Has Manual Alloc = ${hasManualAllocation ? "Yes" : "No"}, Final Available=${toFixed(
            available,
            1
          )}, Allocated = ${toFixed(allocated, 1)}, Manually Allocated = ${toFixed(manuallyAllocated, 1)}, Grouped = ${toFixed(
            fpoGroupedQty,
            1
          )}, Pre-Run Qty = ${toFixed(preRunQty)}, Lot Qty = ${toFixed(lotQty, 1)}`
        );
        return {
          allocOrder,
          style: flowInfo.style,
          color: flowInfo.color,
          fpoQty,
          extraFpoQty,
          fpoExtra: flowInfo.fpoExtra,
          ledgerList: ledgers,
          availableRollList,
          extAvailableRollList,
          fabricItemIds,
          itemCode: ledger?.fabric?.itemCode,
          fabColor: ledger?.fabColor,
          uom: consUom,
          fabricRequired: toFixed(fabricRequired),
          extraFabricRequired: toFixed(extraFabricRequired),
          available: toFixed(available),
          totalAssociation: toFixed(totalAssociation),
          groupedQty: toFixed(fpoGroupedQty),
          allocationQty: prefillAllocationQty ? toFixed(allocationQty) : 0,
          allowedAllocationQty: toFixed(allocationQty),
          lotQty: toFixed(lotQty),
          manuallyAllocated: toFixed(manuallyAllocated),
          shadeReady: toFixed(shadeReady),
          shrinkageReady: toFixed(shrinkageReady),
          qcReady: toFixed(qcReady),
          extAvailable: toFixed(extAvailable),
          hasManualAllocation,
          totalAvailable
        };
      });
    } else {
      const groupOrder = flowInfoRows.find(e => e.key === selectedKey)?.groupOrder;
      const uom = flowInfoRows.find(e => e.key === selectedKey)?.uom;
      if (!isEmpty(groupOrder)) {
        return groupOrder.styleColorList.map(styleColor => {
          const [style, color] = styleColor.split(DEFAULT_SEPARATOR);
          const ledgers = ledgerList.filter(l => l.style === style && l.styleColor === color) || [];
          const ledger = ledgers[0];
          const itemRollList = rollInfoData.filter(
            ri => ri.groupOrderId === groupOrder.id && ri.style === style && ri.color === color && ri.part === groupOrder.part
          );
          const returnedLength = itemRollList.filter(ri => isFalse(ri.active)).reduce((acc, ri) => acc + ri.length, 0);
          const returnedWeight = itemRollList.filter(ri => isFalse(ri.active)).reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const returned =
            uom === UOM.KILOGRAM.value
              ? returnedWeight
              : convert({ input: returnedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });

          const allocatedLength = itemRollList.reduce((acc, ri) => acc + ri.length, 0);
          const allocatedWeight = itemRollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const allocated =
            uom === UOM.KILOGRAM.value
              ? allocatedWeight
              : convert({ input: allocatedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });
          const shadeReadyLength = itemRollList.filter(ri => !isEmpty(ri.shade)).reduce((acc, ri) => acc + ri.length, 0);
          const shadeReadyWeight = itemRollList.filter(ri => !isEmpty(ri.shade)).reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const shadeReady =
            uom === UOM.KILOGRAM.value
              ? shadeReadyWeight
              : convert({ input: shadeReadyLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });
          const qcReadyLength = itemRollList.filter(ri => ri.qcStatus === QcStatus.PASS).reduce((acc, ri) => acc + ri.length, 0);
          const qcReadyWeight = itemRollList.filter(ri => ri.qcStatus === QcStatus.PASS).reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const qcReady =
            uom === UOM.KILOGRAM.value ? qcReadyWeight : convert({ input: qcReadyLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });

          let shadeList = isEmpty(groupOrder.allowedShades) ? [] : JSON.parse(groupOrder.allowedShades);
          shadeList = shadeList
            .map(e => ({
              ...e,
              warehouse: allWhList.find(wh => wh.code === e.warehouse)?.name || e.warehouse,
              allowedShades: listFromCsv(e.allowedShades).join(", ")
            }))
            .filter(e => e.style === style && e.color === color);

          const warehouses = getDistinctValues(shadeList.map(e => e.warehouse));
          const shades =
            shadeList?.length === 1
              ? shadeList[0].allowedShades
              : shadeList
                  .map(
                    e =>
                      `${warehouses.length > 1 ? `${e.warehouse}` : ""}${
                        !isEmpty(e.pattern) ? `${warehouses.length > 1 ? ", " : ""} ${e.pattern}` : ""
                      } --> ${e.allowedShades}`
                  )
                  .join("\n");

          const rollForm = itemRollList[0]?.rollForm;
          return {
            style,
            color,
            warehouse: warehouses.join(", "),
            shades,
            ledgerList: ledgers,
            itemCode: ledger?.fabric?.itemCode,
            fabColor: ledger?.fabColor,
            uom,
            rollForm,
            allocated: toFixed(allocated),
            returned: Number(toFixed(returned)),
            lotQty: toFixed(allocated),
            shadeReady: toFixed(shadeReady),
            qcReady: toFixed(qcReady)
          };
        });
      }
    }
    return [];
  }, [flowInfoRows, selectedKeys, selectedKey, ledgerList, rollInfoList, extRollList, extRollOp, allWhList]);

  const fabricInfoColumns = useDeepMemo(() => {
    const minAssociation = !isEmpty(minRollSplitLength) ? Number(minRollSplitLength) : 0;
    let cols = [
      { dataKey: "style", title: "Style", filterOptions: { filter: true } },
      { dataKey: "color", title: "FG Color", filterOptions: { filter: true } },
      {
        dataKey: "itemCode",
        title: "Item Code",
        linkPath: row =>
          history.push(`/warehouse/quality/${id}/po-view`, {
            flowInfoId: row.id,
            partId: row.part?.id,
            parentId: row.parentId
          })
      },
      { dataKey: "fabColor", title: "Fabric Color" },
      { dataKey: "rollForm", title: "Roll Form" },
      { dataKey: "warehouse", title: "Warehouse" },
      { dataKey: "shades", title: "Shades", length: 1000, render: (value, shortValue) => shortValue || "-" },
      {
        dataKey: "fabricRequired",
        title: "Fab. Required",
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uom]}`)
      },
      {
        dataKey: "extraFabricRequired",
        title: "Fab. Required (Extra)",
        align: "right",
        render: (value, _, row) => `${value} ${UomCodeFromValue[row.uom]} (${100 + (row.fpoExtra || 0)}%)`
      },
      {
        dataKey: "groupedQty",
        title: "Already \nAllcoated",
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uom]}`)
      },
      {
        dataKey: "allocationQty",
        title: "Allocation Qty",
        align: "right",
        inputType: "text-input",
        validate: [required(), minValue(minAssociation + 1, "Must be greater than " + minAssociation)]
        // disabled: row => row.manuallyAllocated > 0
      },
      {
        dataKey: "allocated",
        title: "Allocated Fabric",
        align: "center",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uom]}`)
      },
      {
        dataKey: "returned",
        title: "Returned Fabric",
        align: "center",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uom]}`)
      },
      { dataKey: "shadeReady", title: "Shade Ready", align: "center", render: renderState },
      { dataKey: "shrinkageReady", title: "Shrinkage Ready", align: "center", render: renderState },
      { dataKey: "qcReady", title: "QC Ready", align: "center", render: renderState },
      {
        dataKey: "extAvailable",
        title: "Ext. Usable",
        align: "center",
        render: (value, shortValue, row) => renderState(value, shortValue, row, onExtClick)
      }
    ];
    const styleCount = getDistinctValues(fabricInfoRows.map(e => e.style))?.length;
    const colorCount = getDistinctValues(fabricInfoRows.map(e => e.color))?.length;
    if (styleCount <= 1) {
      cols = cols.filter(c => c.dataKey !== "style");
    }
    if (colorCount <= 1) {
      cols = cols.filter(c => c.dataKey !== "color");
    }

    if (tabIdx === 1) {
      cols = cols.filter(c => !["allocated", "warehouse", "shades", "rollForm"].includes(c.dataKey));
      if (isEmpty(extRollOp)) {
        cols = cols.filter(c => c.dataKey !== "extAvailable");
      }
    } else {
      cols = cols.filter(
        c => !["fabricRequired", "extraFabricRequired", "groupedQty", "allocationQty", "shrinkageReady", "extAvailable"].includes(c.dataKey)
      );
      if (groupOrder?.stateValue >= OState.FINAL_GROUPING_COMPLETED.stateValue) {
        cols = cols.filter(c => !["shadeReady", "qcReady"].includes(c.dataKey));
      } else {
        cols = cols.filter(c => !["returned"].includes(c.dataKey));
      }
    }
    if (allWhList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "warehouse");
    }

    return cols;
  }, [tabIdx, fabricInfoRows, allWhList, groupOrder, minRollSplitLength, extRollOp]);
  //* FabricInfo Table Rows and Columns *//

  const onSelect = selectedIds => {
    const keys = selectedIds.filter(id => !isEmpty(id));
    const selectedRows = flowInfoRows.filter(row => keys.includes(row.key) && !row.isParent);
    setSelectedKeys(selectedRows.map(e => e.key));
  };

  //** Handle Shade Selection on Ready to Plan Tab **/
  const onChange =
    ({ rowIdx, column }) =>
    e => {
      let checked = e.target.checked;
      setShadeState(({ columns, rows }) => {
        let newRows = [...rows];

        const shadeCols = columns.filter(c => c.dataKey.startsWith("shade-"));

        let row = { ...newRows[rowIdx] };
        if (column.dataKey === "selectAll") {
          row = {
            ...newRows[rowIdx],
            ...shadeCols.reduce((acc, c) => ({ ...acc, [c.dataKey]: { checked, value: rows[rowIdx][c.dataKey]?.value } }), {})
          };
        } else {
          const cellValue = rows[rowIdx][column.dataKey];
          row = { ...newRows[rowIdx], [column.dataKey]: { checked, value: cellValue?.value } };
        }
        row = { ...row, selectAll: { checked: shadeCols.every(c => row[c.dataKey]?.checked) } };

        //** Update Row Total **/
        const total = Object.keys(row)
          .filter(key => key.startsWith("shade-"))
          .filter(key => row[key].checked)
          .reduce((acc, key) => acc + (row[key]?.value || 0), 0);
        newRows.splice(rowIdx, 1, { ...row, total: `${toFixed(total, 1)} ${row.uomCode}` });

        //** Update Column Total **//
        if (newRows?.length > 1 && newRows[newRows.length - 1]?.isTotal) {
          const totalRow = newRows[newRows?.length - 1];
          const shadeDataRows = newRows.slice(0, newRows.length - 1);
          const shadeQtyMap = Object.keys(totalRow)
            .filter(key => key.startsWith("shade-"))
            .reduce(
              (acc, key) => ({
                ...acc,
                [key]: toFixed(
                  shadeDataRows.filter(fRow => fRow[key].checked).reduce((acc, fRow) => acc + (fRow[key]?.value || 0), 0),
                  1
                )
              }),
              {}
            );
          const total = toFixed(
            Object.values(shadeQtyMap).reduce((acc, qty) => acc + qty, 0),
            1
          );
          newRows.splice(newRows.length - 1, 1, {
            ...totalRow,
            ...Object.keys(shadeQtyMap).reduce((acc, key) => ({ ...acc, [key]: `${shadeQtyMap[key]} ${row.uomCode}` }), {}),
            total: `${total} ${row.uomCode}`
          });
        }
        return { columns, rows: newRows };
      });
    };

  //** Initialize Shade Columns and Rows */
  useDeepEffect(() => {
    if (isEmpty(flowInfoRows) || isEmpty(selectedKeys)) {
      setShadeState({ columns: [], rows: [] });
      setErrorMessage(null);
      return;
    }
    const selectedRows = flowInfoRows.filter(fi => selectedKeys.includes(fi.key) && !fi.isParent);
    const productCount = getDistinctValues(selectedRows.map(row => row.productName).filter(e => !isEmpty(e)))?.length;
    const uomCount = getDistinctValues(selectedRows.map(row => row.uom).filter(e => !isEmpty(e)))?.length;
    const partCount = getDistinctValues(selectedRows.map(row => row.effPartName).filter(e => !isEmpty(e)))?.length;
    const unitCount = getDistinctValues(selectedRows.map(row => row.unitId).filter(e => !isEmpty(e)))?.length;
    const fpoCount = getDistinctValues(selectedRows.map(row => row.fpo).filter(e => !isEmpty(e)))?.length;
    const parentKeys = getDistinctValues(selectedRows.map(row => row.parentKey));
    const parentList = flowInfoRows.filter(e => parentKeys.includes(e.key));

    const parentProductIds = getDistinctValues(selectedRows.map(e => e.parentProductId).filter(e => !isEmpty(e)));
    const itemCodeList = getDistinctValues(selectedRows.flatMap(e => e.itemCodeList || []).filter(e => !isEmpty(e)));
    const itemColorList = getDistinctValues(selectedRows.flatMap(e => e.itemColorList || []).filter(e => !isEmpty(e)));
    const canCombineProduct = parentProductIds.length === 1 && itemCodeList.length === 1 && itemColorList.length === 1;

    let columns = [];
    let rows = [];
    const hasManualAllocation = fabricInfoRows.some(e => e.hasManualAllocation);
    const totalAvailable = fabricInfoRows.reduce((acc, e) => acc + e.totalAvailable, 0);
    const available = fabricInfoRows.reduce((acc, e) => acc + e.available, 0);

    const invoiceMap = fabricInfoRows.reduce(
      (acc, fi) => ({
        ...acc,
        // [`${fi.style}#@#${fi.color}`]: getDistinctValues(fi.availableRollList.map(ri => ri.ledgerIn?.invoice?.invoiceNo || "")),
        [`${fi.style}#@#${fi.color}`]: getDistinctValues(fi.ledgerList.map(ledger => ledger.invoice?.invoiceNo || ""))
      }),
      {}
    );
    const hasMultipleInvoice = Object.keys(invoiceMap).some(key => invoiceMap[key]?.length > 1);

    if (selectedRows.some(e => isEmpty(e.unitId))) {
      setErrorMessage("Factory Production order must be created for Grouping. Hint: Request PPC team to create Factory Production Order.");
    } else if (uomCount > 1) {
      setErrorMessage("Different Consumption UOM cannot be grouped together");
    } else if (productCount > 1 && !canCombineProduct) {
      setErrorMessage("Different Products cannot be grouped together");
    } else if (partCount > 1) {
      setErrorMessage("Different parts cannot be grouped together");
    } else if (unitCount > 1) {
      setErrorMessage("Different units cannot be grouped together");
    } else if (fpoCount > 1) {
      setErrorMessage("Multiple FPO cannot be grouped together");
    } else if (parentKeys?.length > 1 && parentList[0]?.productName !== selectedRows[0]?.productName) {
      setErrorMessage("Orders across Set cannot be grouped together");
    } else if (hasManualAllocation && available === 0 && totalAvailable > 0) {
      setErrorMessage("Automated allocation not allowed if Manual allocation was done in previous lot.");
    } else if (!isTrue(allowMultiInvoiceAutoAllocation) && hasMultipleInvoice && !hasManualAllocation) {
      setErrorMessage("Automated allocation not supported if fabric available is in multiple invoice. Hint: Allocated fabric manually");
    } else {
      setErrorMessage(null);

      const availableRolls = fabricInfoRows
        .flatMap(e => (isEmpty(extRollOp) ? e.availableRollList || [] : e.extAvailableRollList || []))
        .reduce((acc, roll) => ({ ...acc, [roll.id]: roll }), {});
      const availableRollList = Object.values(availableRolls);
      const shadeList = getDistinctValues(
        availableRollList
          .map(e => e.effShade)
          .filter(shade => !isEmpty(shade))
          .sort((a, b) => (a < b ? -1 : 1))
      );
      const warehouseList = getDistinctValues(availableRollList.map(e => e.currWarehouse));
      if (isEmpty(shadeList)) return;
      const uom = selectedRows[0]?.uom;
      const uomCode = UomCodeFromValue[uom];

      const shadeQtyMap = availableRollList
        .filter(e => !isEmpty(e.effShade))
        .reduce(
          (acc, roll) => ({
            ...acc,
            [roll.effShade]:
              (acc[roll.effShade] || 0) +
              (uom === UOM.KILOGRAM.value
                ? roll.weight || 0
                : convert({ input: roll.length, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList }))
          }),
          {}
        );

      columns = [
        { dataKey: "selectAll", title: "", element: <LabeledCheckBox onChange={onChange} /> },
        { dataKey: "style", title: "Style" },
        { dataKey: "color", title: "Style Color" },
        { dataKey: "warehouse", title: "Warehouse" },
        { dataKey: "pattern", title: "Pattern" },
        ...shadeList.map(shade => ({
          dataKey: `shade-${shade}`,
          title: `${shade}`,
          element: <LabeledCheckBox onChange={onChange} />
        })),
        { dataKey: "total", title: `Total` }
      ];

      const rolls = fabricInfoRows.flatMap(e => e.availableRollList || []);
      const hasManualPattern = rolls.some(r => !isEmpty(r.warpPattern) && !isEmpty(r.weftPattern));

      rows = fabricInfoRows.flatMap(({ style, color, ...row }) => {
        return warehouseList.flatMap(warehouse => {
          const rollList = isEmpty(extRollOp) ? row.availableRollList || [] : row.extAvailableRollList || [];
          let patternList = hasManualPattern ? getDistinctValues(rollList.filter(r => !isEmpty(r.pattern)).map(r => r.pattern)) : [""];

          return patternList
            .map(pattern => {
              const shadeQtyMap = rollList
                .filter(e => e.currWarehouse === warehouse)
                .filter(e => isEmpty(pattern) || e.pattern === pattern)
                .filter(e => !isEmpty(e.effShade))
                .reduce(
                  (acc, roll) => ({
                    ...acc,
                    [roll.effShade]:
                      (acc[roll.effShade] || 0) +
                      (uom === UOM.KILOGRAM.value
                        ? roll.weight || 0
                        : convert({ input: roll.length, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList }))
                  }),
                  {}
                );

              return {
                id: row.id,
                style,
                color,
                whCode: warehouse,
                uomCode,
                pattern,
                warehouse: allWhList.find(e => e.code === warehouse)?.name || warehouse,
                association: row.allocationQty,
                shadeReady: row.shadeReady,
                selectAll: { checked: prefillAllocationQty },
                available: toFixed(Object.values(shadeQtyMap).reduce((acc, qty) => acc + qty, 0)),
                total: prefillAllocationQty ? `${toFixed(Object.values(shadeQtyMap).reduce((acc, qty) => acc + qty, 0))} ${uomCode}` : `0 ${uomCode}`,
                ...shadeList.reduce(
                  (acc, shade) => ({
                    ...acc,
                    [`shade-${shade}`]: { checked: prefillAllocationQty, value: isEmpty(shadeQtyMap[shade]) ? 0 : toFixed(shadeQtyMap[shade], 1) }
                  }),
                  {}
                )
              };
            })
            .filter(e => e.available > 0);
        });
      });
      const styleCount = getDistinctValues(selectedRows.map(e => e.style))?.length;
      const colorCount = getDistinctValues(selectedRows.map(e => e.color))?.length;

      if (styleCount <= 1) {
        columns = columns.filter(c => c.dataKey !== "style");
      }
      if (colorCount <= 1) {
        columns = columns.filter(c => c.dataKey !== "color");
      }
      if (!hasManualPattern) {
        columns = columns.filter(c => c.dataKey !== "pattern");
      }

      if (rows?.length > 1) {
        let totalRow = {
          isTotal: true,
          [columns[1].dataKey]: "Total",
          total: prefillAllocationQty
            ? `${toFixed(
                Object.values(shadeQtyMap).reduce((acc, qty) => acc + qty, 0),
                1
              )} ${uomCode}`
            : `0 ${uomCode}`,
          ...shadeList.reduce(
            (acc, shade) => ({ ...acc, [`shade-${shade}`]: prefillAllocationQty ? `${toFixed(shadeQtyMap[shade], 0)} ${uomCode}` : `0 ${uomCode}` }),
            {}
          )
        };
        rows = [...rows, totalRow];
      }
    }

    setShadeState({ columns, rows });
  }, [fabricInfoRows, flowInfoRows, selectedKeys, allWhList, extRollOp]);

  useDeepEffect(() => {
    if (tabIdx !== 0 || isEmpty(flowInfoRows)) {
      setSelectedKey(null);
    } else {
      const key = flowInfoRows.filter(f => !f.isParent)[0]?.key;
      setSelectedKey(key);
    }
  }, [tabIdx, flowInfoRows]);

  const runShrinkageGrouping = (values, _a, _b, fabRows) => {
    if (!isEmpty(errorMessage)) return;

    const selectedRows = flowInfoRows.filter(fi => selectedKeys.includes(fi.key));

    if (isEmpty(selectedRows)) return;

    const uom = selectedRows[0]?.uom;

    let shadeErrors = [];
    let allocErrors = [];
    let allocWarnings = [];
    let whErrors = [];
    const flowInfoList = selectedRows.map(row => {
      const shadeRowList = shadeRows.filter(e => e.style === row.style && e.color === row.color);
      const fabricRow = isEmpty(fabRows)
        ? fabricInfoRows.find(e => e.style === row.style && e.color === row.color)
        : fabRows.find(e => e.style === row.style && e.color === row.color);
      const allowedAllocationQty = fabricRow?.allowedAllocationQty || 0;
      const associationQty = fabricRow?.allocationQty || 0;
      if (associationQty > allowedAllocationQty) {
        allocErrors.push(`${row.style} -> ${row.color}: Current allocation = ${associationQty}, Allowed Allocation = ${allowedAllocationQty}`);
      }
      if (associationQty > fabricRow?.extraFabricRequired + 1) {
        allocWarnings.push(
          `Fabric Over allocation. Fabric required for ${fabricRow.extraFpoQty} pcs as per BOM consumption is - ${fabricRow.extraFabricRequired} ${UomCodeFromValue[uom]} (${row.style} -> ${row.color})`
        );
      }

      const fpoWhList = allWhList.filter(e => e.unit?.unitId === row?.unitId);

      const allowedShadeList = shadeRowList
        .map(shadeRow => {
          const allowedShades = Object.keys(shadeRow)
            .filter(key => key.startsWith("shade-"))
            .filter(key => shadeRow[key].checked && shadeRow[key].value > 0)
            .map(key => key.substring(6))
            .join(",");
          const shades = Object.keys(shadeRow)
            .filter(key => key.startsWith("shade-"))
            .filter(key => shadeRow[key].checked);
          const shadeQty = shades.map(shade => shadeRow[shade]?.value || 0).reduce((acc, qty) => acc + qty, 0);
          const totalQty = Object.keys(shadeRow)
            .filter(key => key.startsWith("shade-"))
            .map(shade => shadeRow[shade]?.value || 0)
            .reduce((acc, qty) => acc + qty, 0);

          return {
            style: shadeRow.style,
            color: shadeRow.color,
            warehouse: shadeRow.whCode,
            pattern: shadeRow.pattern,
            allowedShades,
            shadeQty,
            totalQty
          };
        })
        .filter(e => e.shadeQty > 0);
      const shadeQty = allowedShadeList.reduce((acc, e) => acc + e.shadeQty, 0);
      // const totalQty = allowedShadeList.reduce((acc, e) => acc + e.totalQty, 0);
      if (toFixed(shadeQty, 3) - toFixed(Number(associationQty), 3) < -0.1) {
        shadeErrors.push(
          `${row.style} -> ${row.color}: Current association = ${toFixed(Number(associationQty), 3)}, Shade Qty = ${toFixed(shadeQty, 3)}`
        );
      }
      if (fpoWarehouse && shadeQty > 0 && allowedShadeList.some(w => !fpoWhList.map(e => e.code).includes(w.warehouse))) {
        whErrors.push(`Fabric must be selected from [${fpoWhList.map(e => e.name).join(", ")}] only (${row.style} -> ${row.color}).`);
      }

      const availableRollList = isEmpty(extRollOp) ? fabricRow?.availableRollList || [] : fabricRow?.extAvailableRollList || [];

      return {
        parentProductId: row.parentProductId,
        productId: row.product?.id,
        style: row.style,
        color: row.color,
        parentColor: row.parentColor,
        effPartName: row.part?.effPartName,
        uom: row.uom,
        lot: row?.lot || 1,
        fpo: row.fpo,
        unitId: row.unitId,
        associationQty,
        allowedShades: JSON.stringify(allowedShadeList),
        availableRollList: availableRollList?.map(e => ({ id: e.id, rollNo: e.rollNo })) || []
      };
    });

    if (!isEmpty(allocErrors)) {
      dispatch(
        showNotification(`Allocation qty cannot exceed maximum allowed allocation qty. ${allocErrors.join(", ")}`, {
          type: "danger",
          autoHideDuration: 10000
        })
      );
      return;
    }
    if (!isEmpty(shadeErrors)) {
      dispatch(
        showNotification(`Shade qty cannot be less than current association. ${shadeErrors.join(", ")}`, {
          type: "danger",
          autoHideDuration: 10000
        })
      );
      return;
    }
    if (!isEmpty(whErrors)) {
      dispatch(
        showNotification(`Fabric selection allowed from FPO unit only. ${whErrors.join(", ")}`, {
          type: "danger",
          autoHideDuration: 10000
        })
      );
      return;
    }

    let confirmed = true;
    if (!isEmpty(allocWarnings)) {
      // eslint-disable-next-line no-restricted-globals
      confirmed = confirm(`${allocWarnings?.join("\n\r")}\n\rAre you sure you want proceed?`);
    }
    if (!confirmed) {
      return;
    }

    const options = {
      url: `groups/run`,
      method: "post",
      params: { orderId: id, action: "shrinkage" },
      data: { setting: values, flowInfoList }
    };

    mutate(options, {
      onSuccess: data => {
        if (!isEmpty(data?.shrinkageOptions)) {
          const shrinkageOptions = data.shrinkageOptions.map(option => ({
            ...option,
            groupList:
              option.groupList?.map(group => ({
                ...group,
                warp: `${toFixed(group.warpMean, 1)} ${shrinkageRange(group, "warp")}`,
                weft: `${toFixed(group.weftMean, 1)} ${shrinkageRange(group, "weft")}`,
                deviations: getDeviation(group),
                fabricQty: getGroupFabricQty(group, uom, conversionFactorList)
              })) || []
          }));
          setSetting(values);
          setFabRows(fabRows);
          setShrinkageOptions(shrinkageOptions);
        } else {
          dispatch(showNotification(`Groups saved successfully.`));
          setSetting({});
          setFabRows();
          setShrinkageOptions([]);
          setWidthOptionState({});
          setVersion(v => v + 1);
          setTabIdx(0);
        }
      }
    });
  };

  const runFinalGrouping = (values, _a, _b, fabRows) => {
    if (!isEmpty(errorMessage)) return;

    let flowInfoList = [];
    let uom;
    if (tabIdx === 1) {
      const selectedRows = flowInfoRows.filter(fi => selectedKeys.includes(fi.key));
      if (isEmpty(selectedRows)) return;
      uom = selectedRows[0]?.uom;

      let shadeErrors = [];
      let allocErrors = [];
      let allocWarnings = [];
      let whErrors = [];
      flowInfoList = selectedRows.map(row => {
        const shadeRowList = shadeRows.filter(e => e.style === row.style && e.color === row.color);
        const fabricRow = isEmpty(fabRows)
          ? fabricInfoRows.find(e => e.style === row.style && e.color === row.color)
          : fabRows.find(e => e.style === row.style && e.color === row.color);
        const allowedAllocationQty = fabricRow?.allowedAllocationQty || 0;
        const associationQty = fabricRow?.allocationQty || 0;
        if (associationQty > allowedAllocationQty) {
          allocErrors.push(`${row.style} -> ${row.color}: Current allocation = ${associationQty}, Allowed Allocation = ${allowedAllocationQty}`);
        }
        if (associationQty > fabricRow?.extraFabricRequired + 1) {
          allocWarnings.push(
            `Fabric Over allocation. Fabric required for ${fabricRow.extraFpoQty} pcs as per BOM consumption is - ${fabricRow.extraFabricRequired} m (${row.style} -> ${row.color})`
          );
        }

        const fpoWhList = allWhList.filter(e => e.unit?.unitId === row?.unitId);

        const allowedShadeList = shadeRowList
          .map(shadeRow => {
            const allowedShades = Object.keys(shadeRow)
              .filter(key => key.startsWith("shade-"))
              .filter(key => shadeRow[key].checked && shadeRow[key].value > 0)
              .map(key => key.substring(6))
              .join(",");
            const shades = Object.keys(shadeRow)
              .filter(key => key.startsWith("shade-"))
              .filter(key => shadeRow[key].checked);
            const shadeQty = shades.map(shade => shadeRow[shade]?.value || 0).reduce((acc, qty) => acc + qty, 0);
            const totalQty = Object.keys(shadeRow)
              .filter(key => key.startsWith("shade-"))
              .map(shade => shadeRow[shade]?.value || 0)
              .reduce((acc, qty) => acc + qty, 0);

            return { style: shadeRow.style, color: shadeRow.color, warehouse: shadeRow.whCode, allowedShades, shadeQty, totalQty };
          })
          .filter(e => e.shadeQty > 0);

        const shadeQty = allowedShadeList.reduce((acc, e) => acc + e.shadeQty, 0);
        if (toFixed(shadeQty, 3) - toFixed(Number(associationQty), 3) < -0.1) {
          shadeErrors.push(
            `${row.style} -> ${row.color}: Current association = ${toFixed(Number(associationQty), 3)}, Shade Qty = ${toFixed(shadeQty, 3)}, `
          );
        }
        if (fpoWarehouse && shadeQty > 0 && allowedShadeList.some(w => !fpoWhList.map(e => e.code).includes(w.warehouse))) {
          whErrors.push(`Fabric must be selected from [${fpoWhList.map(e => e.name).join(", ")}] only (${row.style} -> ${row.color}).`);
        }

        const availableRollList = isEmpty(extRollOp) ? fabricRow?.availableRollList || [] : fabricRow?.extAvailableRollList || [];

        return {
          productId: row.product?.id,
          style: row.style,
          color: row.color,
          parentColor: row.parentColor,
          effPartName: row.part?.effPartName,
          uom: row.uom,
          lot: row?.lot || 1,
          unitId: row.unitId,
          associationQty,
          allowedShades: JSON.stringify(allowedShadeList),
          availableRollList: availableRollList?.map(e => ({ id: e.id, rollNo: e.rollNo })) || []
        };
      });

      if (!isEmpty(allocErrors)) {
        dispatch(
          showNotification(`Allocation qty cannot exceed maximum allowed allocation qty. ${allocErrors.join(", ")}`, {
            type: "danger",
            autoHideDuration: 10000
          })
        );
        return;
      }

      if (!isEmpty(shadeErrors)) {
        dispatch(
          showNotification(`Shade qty cannot be less than current association. ${shadeErrors.join(", ")}`, {
            type: "danger",
            autoHideDuration: 10000
          })
        );
        return;
      }

      if (!isEmpty(whErrors)) {
        dispatch(
          showNotification(`Fabric selection allowed from FPO unit only. ${whErrors.join(", ")}`, {
            type: "danger",
            autoHideDuration: 10000
          })
        );
        return;
      }

      let confirmed = true;
      if (!isEmpty(allocWarnings)) {
        // eslint-disable-next-line no-restricted-globals
        confirmed = confirm(`${allocWarnings?.join("\n\r")}\n\rAre you sure you want proceed?`);
      }
      if (!confirmed) {
        return;
      }
    } else {
      uom = flowInfoRows.find(e => e.key === selectedKey)?.uom;
    }

    const options = {
      url: `groups/run`,
      method: "post",
      params: { orderId: id, groupOrderId: groupOrder?.id, action: "final" },
      data: { setting: values, flowInfoList }
    };

    mutate(options, {
      onSuccess: data => {
        if (!isEmpty(data.widthOptions)) {
          const widthOptions = data.widthOptions
            .map(option => {
              return {
                ...option,
                groupList: option.groupList.map(group => ({
                  ...group,
                  width: `${group.widthAxis} (${toFixed(group.widthAxis, 1)} - ${toFixed(group.widthAxis + group.widthVariance, 1)})`,
                  fabricQty: getGroupFabricQty(group, uom, conversionFactorList)
                })),
                count: option.groupList?.length
              };
            })
            .sort((a, b) => a.count - b.count);
          const optionSelected = widthOptions.find(option => option.selected);
          const defaultWidthGroups = optionSelected ? optionSelected.groupList.length : -1;
          const finalOption = getFinalOption(data.summarySelected);

          setWidthOptionState({ flowInfoList: [], setting: values, widthOptions, finalOption, defaultWidthGroups });
          setFabRows(fabRows);
        }
      }
    });
  };

  const updatePattern = (rows, _, onActionComplete) => {
    const options = {
      url: `groups`,
      method: "put",
      data: rows.map(e => ({ ...e, warpPattern: e.warpManual, weftPattern: e.weftManual })),
      params: { orderId: id, groupOrderId: groupOrder?.id }
    };
    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Pattern updated successfully."));
        setVersion(v => v + 1);
        onActionComplete && onActionComplete();
      },
      onFailure: () => onActionComplete && onActionComplete()
    });
  };

  const handleSelection = handleSubmit => (type, optionIndex) => {
    if (type === "shrinkage") {
      setShrinkageOptions(options => options.map((option, idx) => ({ ...option, selected: idx === optionIndex })));
    } else if (type === "final") {
      if (!isEmpty(widthOptionState?.widthOptions)) {
        const widthOptions = widthOptionState.widthOptions.map((option, idx) => ({
          ...option,
          selected: idx === optionIndex
        }));

        setWidthOptionState(prev => ({ ...prev, widthOptions }));
        const { setting, defaultWidthGroups } = widthOptionState;
        const widthSummary = widthOptions.find(option => option.selected);

        const options = {
          url: `groups/run`,
          method: "post",
          params: { orderId: id, groupOrderId: groupOrder?.id, action: "select" },
          data: { setting, widthSummary, defaultWidthGroups, lot: groupOrder?.lot }
        };

        mutate(options, {
          onSuccess: data => {
            if (!isEmpty(data.summarySelected)) {
              const finalOption = getFinalOption(data.summarySelected);
              setWidthOptionState(prev => ({ ...prev, finalOption }));
            }
          }
        });
      }
    }
  };

  const onSave = () => {
    let flowInfoList = [];
    if (tabIdx === 1) {
      const selectedRows = flowInfoRows.filter(fi => selectedKeys.includes(fi.key));
      if (isEmpty(selectedRows)) return;

      let errors = [];
      flowInfoList = selectedRows.map(row => {
        const shadeRowList = shadeRows.filter(e => e.style === row.style && e.color === row.color);
        const fabricRow = isEmpty(fabRows)
          ? fabricInfoRows.find(e => e.style === row.style && e.color === row.color)
          : fabRows.find(e => e.style === row.style && e.color === row.color);
        const associationQty = fabricRow?.allocationQty || 0;

        const allowedShadeList = shadeRowList
          .map(shadeRow => {
            const allowedShades = Object.keys(shadeRow)
              .filter(key => key.startsWith("shade-"))
              .filter(key => shadeRow[key].checked && shadeRow[key].value > 0)
              .map(key => key.substring(6))
              .join(",");
            const shades = Object.keys(shadeRow)
              .filter(key => key.startsWith("shade-"))
              .filter(key => shadeRow[key].checked);
            const shadeQty = shades.map(shade => shadeRow[shade]?.value || 0).reduce((acc, qty) => acc + qty, 0);
            const totalQty = Object.keys(shadeRow)
              .filter(key => key.startsWith("shade-"))
              .map(shade => shadeRow[shade]?.value || 0)
              .reduce((acc, qty) => acc + qty, 0);

            return { style: shadeRow.style, color: shadeRow.color, warehouse: shadeRow.whCode, allowedShades, shadeQty, totalQty };
          })
          .filter(e => e.shadeQty > 0);

        const shadeQty = allowedShadeList.reduce((acc, e) => acc + e.shadeQty, 0);
        const totalQty = allowedShadeList.reduce((acc, e) => acc + e.totalQty, 0);
        if (toFixed(shadeQty, 3) < toFixed(totalQty, 3) && toFixed(shadeQty, 3) - toFixed(Number(associationQty), 3) < -0.1) {
          errors.push(
            `${row.style} -> ${row.color}: Current association = ${toFixed(Number(associationQty), 3)}, Shade Qty = ${toFixed(shadeQty, 3)}, `
          );
        }

        return {
          productId: row.product?.id,
          style: row.style,
          color: row.color,
          parentColor: row.parentColor,
          parentProductId: row.parentProductId,
          effPartName: row.part?.effPartName,
          uom: row.uom,
          lot: row?.lot || 1,
          unitId: row.unitId,
          fpo: row.fpo,
          associationQty,
          allowedShades: JSON.stringify(allowedShadeList)
        };
      });

      if (!isEmpty(errors)) {
        dispatch(showNotification(`Shade qty cannot be less than current association. [${errors.join(", ")}]`, { type: "danger" }));
        return;
      }
    }

    const finalOption = widthOptionState?.finalOption || {};
    const groups = !isEmpty(shrinkageOptions) ? shrinkageOptions.find(option => option.selected).groupList : finalOption.groupList || [];

    let finalSetting = setting;
    if (!isEmpty(finalOption)) {
      const groupingWastage = `${toFixed(finalOption.widthWasteLength)} m (${toFixed(finalOption.widthWastePercent)}%)`;
      finalSetting = { ...finalSetting, groupingWastage, widthWastePercent: finalOption.widthWastePercent };
    }

    const options = {
      url: `groups`,
      method: "post",
      params: { orderId: id, groupOrderId: groupOrder?.id, action: !isEmpty(shrinkageOptions) ? "shrinkage" : "final" },
      data: { setting: finalSetting, groups, flowInfoList }
    };

    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification(`Groups saved successfully.`));
        setSetting({});
        setFabRows();
        setShrinkageOptions([]);
        setWidthOptionState({});
        setVersion(v => v + 1);
        setTabIdx(0);
      }
    });
  };

  const onDelete = (onActionComplete, rows) => {
    const options = {
      url: `groups`,
      method: "delete",
      params: { orderId: id, groupOrderId: groupOrder?.id }
    };

    mutate(options, {
      onSuccess: data => {
        setVersion(version => version + 1);
        dispatch(showNotification("Groups deleted successfully."));
        onActionComplete && onActionComplete(rows);
      },
      onFailure: () => {
        onActionComplete && onActionComplete(rows);
      }
    });
  };

  const uom =
    tabIdx === 0
      ? flowInfoRows.find(e => e.key === selectedKey)?.uom
      : flowInfoRows.filter(row => selectedKeys.includes(row.key) && !row.isParent)[0]?.uom;

  const onDownload = (onActionComplete, rows) => {
    const unit = allUnitList?.find(e => e.unitId === groupOrder?.unitId) || {};
    const excelSheetList = generateGroupingReportData({
      tenantName: unit?.displayName || unit?.name || tenantName || "",
      order,
      orderFlow: groupOrder,
      flowInfoColumns: flowInfoColumns.map(c => ({
        ...c,
        title: lodashGet(messages, `resources.grouping.fields.flowInfo.${c.dataKey}`) || c.title
      })),
      flowInfoRows: flowInfoRows.filter(fi => fi.key === selectedKey),
      rollInfoList,
      groupList: groupList.map(group => ({ ...group, totalFabric: getGroupFabricQty(group, uom, conversionFactorList) })),
      rollFields,
      i18nMessages: messages
    });
    if (isEmpty(excelSheetList)) return;

    const filename = `GROUPING_REPORT_${order?.poRef?.replaceAll("/", "-")}`;
    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      params: { filename },
      data: excelSheetList
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var fileName = header ? header.match(/filename="(.+)"/)[1] : filename;

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
        }
        onActionComplete && onActionComplete(rows);
      },
      onFailure: () => onActionComplete && onActionComplete(rows)
    });
  };

  const onCancel = () => {
    setShrinkageOptions([]);
    setWidthOptionState({});
  };

  return {
    busy,
    version,
    conversionFactorList,
    errorMessage,
    selectedKeys,
    selectedKey,
    order,
    flowInfoColumns,
    fabricInfoColumns,
    shadeColumns,
    flowInfoRows,
    fabricInfoRows,
    shadeRows,
    tabIdx,
    shrinkageOptions,
    widthOptionState,
    groupOrder,
    groupList,
    setTabIdx,
    setSelectedKeys,
    setSelectedKey,
    minRollSplitLength,
    ledgerList,
    extRollList,
    extDrawerState,
    setExtDrawerState,
    onSelect,
    runShrinkageGrouping,
    runFinalGrouping,
    updatePattern,
    handleSelection,
    onDownload,
    onSave,
    onDelete,
    onCancel,
    rollInfoList: rollInfoData
  };
};

export default useGroupingView;
